import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import dayjs, { Dayjs } from "dayjs";

import { globalStore } from "@redux/store";
import { isSystemIntegratedSelector } from "@redux/features/auth/authSlice";
import { Customer } from "@/types/general";

import GeneralTab from "@components/Dashboard/pages/Customers/Customer/components/tabs/GeneralTab";
import DiscountTab from "./DiscountTab";
import AddressTab from "@components/Dashboard/pages/Customers/Customer/components/tabs/AddressTab";
import SettingsTab from "./SettingsTab";

import { requiredIfAnyFilled } from "@helpers/utils";
import { refinements } from "@helpers/refinements";
import BaseButton from "@reusables/BaseButton";
import BaseTabs from "@reusables/BaseTabs";
import { useHistory } from "react-router-dom";

const ORDER_PRICE_PRECISION = import.meta.env.VITE_ORDER_PRICE_PRECISION;

const maxDecimalPrecisionRefinement = refinements.maxDecimalPrecision(ORDER_PRICE_PRECISION);

const validCRN = refinements.validateCRN;

export const contactScheme = z.array(
    z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        phone: z.string().nullish(),
        email: z.string().email().nullish()
    }).superRefine((scheme, ctx) => {
        requiredIfAnyFilled(scheme, ["email", "phone"])
            .forEach(key => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: {
                        i18n: "general.validation.global.required"
                    },
                    path: [key]
                });
            });
    })
);

export const personContactScheme = z.array(
    z.object({
        id: z.number().optional(),
        phone: z.string().optional(),
        email: z.string().email().optional()
    }).superRefine((scheme, ctx) => {
        requiredIfAnyFilled(scheme)
            .forEach(key => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: {
                        i18n: "general.validation.global.required"
                    },
                    path: [key]
                });
            });
    })
);

const discountScheme = z.object({
    id: z.number().optional(),
    type: z.enum(["product", "category"]),
    product: z.object({
        id: z.number(),
        code: z.string(),
        name: z.string(),
    }).nullish(),
    category: z.object({
        id: z.number(),
        code: z.string().nullish(),
        name: z.string(),
    }).nullish(),
    sale_price: z.coerce.number().min(0).optional(),
    discount: z.coerce.number().min(0).max(100).optional(),
    min_quantity: z.coerce.number().min(0).optional(),
    valid_from: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),
    valid_to: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),
})

const formScheme = z.object({
    mode: z.union([z.literal("creation"), z.literal("editing")]),

    name: z.string().trim().nonempty(),
    code: z.string().trim().optional(),
    registration_number: z.string().trim().refine(validCRN.refine, validCRN.message).optional(),

    is_person: z.boolean(),

    contacts: contactScheme.nullish(),

    person_contacts: personContactScheme.nullish(),

    deleted_contacts: z.array(z.number()),

    billing_address: z.object({
        name: z.string().nullish(),
        street: z.string().nullish(),
        street_2: z.string().nullish(),
        zipcode: z.string().nullish(),
        city: z.string().nullish(),
        country: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string()
        }).nullish(),
        phone: z.string().nullish(),
        email: z.string().email().nullish(),
        is_used_for_shipping: z.boolean().nullish()
    }).superRefine((scheme, ctx) => {
        const hasNonNullishFields = _.some(
            _.omit(scheme, ["name", "is_used_for_shipping"]),
            (value) => !_.isNil(value) && value !== ""
        );
    
        if (hasNonNullishFields && (!scheme.name || scheme.name.trim() === "")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.required"
                },
                path: ["name"],
            });
        }

        if(isSystemIntegratedSelector("poweroffice")(globalStore.getState()) && (!scheme.email || scheme.email.trim() === "")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "customers.general.tabs.address.fields.email.validation.requiredByPOGO"
                },
                path: ["email"],
            });
        }
    }),

    deliveries: z.array(
        z.object({
            id: z.number().optional(),
            name: z.string().nullish(),
            street: z.string().nullish(),
            street_2: z.string().nullish(),
            zipcode: z.string().nullish(),
            city: z.string().nullish(),
            country: z.object({
                id: z.number(),
                name: z.string(),
                code: z.string()
            }).nullish(),
            contact_person: z.string().nullish(),
            phone: z.string().nullish(),
            email: z.string().email().nullish(),
            is_primary: z.boolean().nullish()
        }).superRefine((scheme, ctx) => {
            const hasNonNullishFields = _.some(
                _.omit(scheme, ["name", "is_primary", "id"]),
                (value) => !_.isNil(value) && value !== ""
            );
        
            if (hasNonNullishFields && (!scheme.name || scheme.name.trim() === "")) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: {
                        i18n: "general.validation.global.required"
                    },
                    path: ["name"],
                });
            }
        })
    ).nullish(),

    deleted_deliveries: z.array(z.number()),

    language: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string()
    }).nullish(),

    tax_rate: z.object({
        id: z.number(),
        name: z.string().nullish(),
        rate: z.number()
    }).nullish(),

    discount: z.coerce.number().min(0).max(100).nullish(),

    payment_terms: z.object({
        id: z.number(),
        name: z.string()
    }).nullish(),

    delivery_terms: z.object({
        id: z.number(),
        name: z.string()
    }).nullish(),

    reference: z.object({
        id: z.number(),
        name: z.string()
    }).nullish(),

    discounts: z.array(discountScheme).nullish(),
    deleted_category_discounts: z.array(z.number()).nullish()
});

export type CustomerFormTyping = z.infer<typeof formScheme>

interface TabsControllerProps {
    customer?: Customer.Extended;
    onSubmit: (data: CustomerFormTyping) => void;
}

export default function TabsController({ customer, onSubmit }: TabsControllerProps) {
    const { t } = useTranslation("", { keyPrefix: "customers.general" });

    const [activeTab, setActiveTab] = useState<string>("general");

    const methods = useForm<CustomerFormTyping>({
        resolver: zodResolver(formScheme),
        defaultValues: {
            contacts: [{}],
            person_contacts: [{}],
            deliveries: [{}],
            deleted_contacts: [],
            deleted_deliveries: [],
            deleted_category_discounts: [],
            is_person: false
            // products: [{}]
        }
    });

    const onFormSubmit = methods.handleSubmit(onSubmit, console.error);

    useEffect(() => {
        if (customer) {
            const isPerson = customer.is_person ?? false;

            const data = {
                mode: "editing" as CustomerFormTyping["mode"],
                is_person: isPerson,
                name: customer.name,
                code: customer.code,
                registration_number: customer.registration_number ?? undefined,
                ...(!isPerson && { contacts: customer.contacts }),
                ...(isPerson && { contacts: customer.contacts }),
                billing_address: customer.billing ?? undefined,
                deliveries: customer.deliveries.length ? customer.deliveries : [{}],
                language: customer.language ?? undefined,
                tax_rate: customer.tax_rate,
                discount: customer.discount ?? undefined,
                payment_terms: customer.payment_terms ?? undefined,
                delivery_terms: customer.delivery_terms ?? undefined,
                reference: customer.reference ?? undefined,
                deleted_contacts: [],
                deleted_deliveries: [],
                discounts: [
                    ...(customer.products
                        ? customer.products.map((product) => ({
                                ...product,
                                type: "product" as const,
                            }))
                        : []),
                    ...(customer.category_discounts
                        ? customer.category_discounts.map((category) => ({
                                ...category,
                                type: "category" as const,
                            }))
                        : []),
                ],
                deleted_category_discounts: [],
            };

            methods.reset(data);
        } else {
            methods.reset({
                mode: "creation",
                is_person: false,
                contacts: [{}],
                person_contacts: [{}],
                deliveries: [{}],
                deleted_contacts: [],
                deleted_deliveries: []
                // products: [{}]
            });
        }
    }, [customer, methods.setValue]);

    const customerHeaderElement = document.getElementById("customer-header");

    return (
        <FormProvider {...methods}>
            {
                customer && customerHeaderElement && createPortal(
                    <BaseButton
                        text={t("save")}
                        size={"md"}
                        onClick={() => onFormSubmit()}
                    />,
                    customerHeaderElement
                )
            }
            <BaseTabs
                value={activeTab}
                onChange={(newKey) => {
                    setActiveTab(newKey);
                }}
                tabs={[
                {
                    key: "general",
                    title: t("tabs.general.heading"),
                    content: <GeneralTab
                                    onNext={e => {
                                        e.preventDefault();
                                        setActiveTab("discount");
                                    }}
                                    mode={customer ? "editing" : "creation"}
                                />
                },
                {
                    key: "discount",
                    title: t("tabs.pricesAndDiscounts.heading"),
                    content: <DiscountTab
                                    onPrev={() => {
                                        setActiveTab("general");
                                    }}
                                    onNext={e => {
                                        e.preventDefault();
                                        setActiveTab("address");
                                    }}
                                    mode={customer ? "editing" : "creation"}
                                />
                },
                {
                    key: "address",
                    title: t("tabs.address.heading"),
                    content: <AddressTab
                                    onPrev={() => {
                                        setActiveTab("discount");
                                    }}
                                    onNext={e => {
                                        e.preventDefault();
                                        setActiveTab("settings");
                                    }}
                                    mode={customer ? "editing" : "creation"}
                                />
                },
                {
                    key: "settings",
                    title: t("tabs.settings.heading"),
                    content: <SettingsTab
                                    onPrev={() => {
                                        setActiveTab("address");
                                    }}
                                    onNext={onFormSubmit}
                                    mode={customer ? "editing" : "creation"}
                                />
                },
            ]} />
        </FormProvider>
    );
}